import { useState } from "react";
import { motion } from "framer-motion";
import {
  Users,
  User,
  CheckCircle2,
  Plane,
  Calendar,
  BrainCircuit,
  ShieldCheck,
  Sparkles,
  Clock,
  FileCheck2,
  MessageCircle,
  Award,
  TrendingUp,
  Zap,
  Target,
  ArrowRight,
  Calculator,
  BadgeCheck,
  Video,
  CreditCard,
} from "lucide-react";
import { Button } from "./ui/button";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/dividelo";

// Payment links (Niubiz Peru) with 20% HOY discount
const PAYMENT_LINKS = {
  1: "https://pagolink.niubiz.com.pe/pagoseguro/CLUBVIAJEMOSPERUSAC/6911356",
  2: "https://pagolink.niubiz.com.pe/pagoseguro/CLUBVIAJEMOSPERUSAC/6911362",
  3: "https://pagolink.niubiz.com.pe/pagoseguro/CLUBVIAJEMOSPERUSAC/6911366",
  4: "https://pagolink.niubiz.com.pe/pagoseguro/CLUBVIAJEMOSPERUSAC/6911370",
  5: "https://pagolink.niubiz.com.pe/pagoseguro/CLUBVIAJEMOSPERUSAC/6911374",
};

// Discounted prices (20% off)
const DISCOUNT_PRICES = {
  1: { original: 99, discounted: 79 },
  2: { original: 150, discounted: 120 },
  3: { original: 225, discounted: 180 },
  4: { original: 300, discounted: 240 },
  5: { original: 375, discounted: 300 },
};

const ADVANCEMENT_STRIPE_URL = "https://buy.stripe.com/5kQ8wO4Uw3m3aeBbgods81o";
const FAMILY_PRICE_PER_PERSON = 75;
const FAMILY_PRICE_PER_PERSON_DISCOUNTED = 60;

const ADVISORY_FEATURES = [
  { icon: BrainCircuit, label: "Análisis IA del perfil consular (Fase 0)" },
  { icon: ShieldCheck, label: "Perfil migratorio verificado y fortalecido" },
  { icon: FileCheck2, label: "Llenado completo del DS-160 · IMM5257 · formulario consular" },
  { icon: Target, label: "Simulación de entrevista con IA" },
  { icon: Award, label: "Revisión por ex cónsules calificados" },
  { icon: MessageCircle, label: "Soporte WhatsApp directo con asesor dedicado" },
  { icon: TrendingUp, label: "Monitoreo del caso hasta la aprobación" },
  { icon: BadgeCheck, label: "Índice de éxito 95% en casos aprobados" },
];

const ADVANCEMENT_FEATURES = [
  { label: "Monitoreo 24/7 de disponibilidad consular" },
  { label: "Gestión directa con el consulado o VAC" },
  { label: "Promedio real de 300 días calendario de adelanto" },
  { label: "Alertas automáticas por WhatsApp al abrir cupo" },
  { label: "Confirmación inmediata al asegurar la cita" },
  { label: "Sin letra chica · Cobramos solo los días efectivamente adelantados" },
];

const AgentPricingSection = () => {
  const [familyCount, setFamilyCount] = useState(2);
  const familyPrices = DISCOUNT_PRICES[familyCount];
  const familyTotal = familyPrices.discounted;
  const familyOriginal = familyPrices.original;
  const familySavings = familyOriginal - familyTotal;
  const familyPaymentUrl = PAYMENT_LINKS[familyCount];

  return (
    <section
      id="precios"
      className="relative py-20 px-6 bg-gradient-to-b from-navy-50 via-white to-navy-50 overflow-hidden"
      data-testid="landing-pricing"
    >
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #1e3a8a 1px, transparent 1px), linear-gradient(to bottom, #1e3a8a 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="max-w-6xl mx-auto relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
          data-testid="pricing-header"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-200 bg-red-50 text-red-700 text-xs uppercase tracking-widest font-black mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            Precios transparentes · Sin letra chica
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-navy-900 mb-5 leading-tight">
            Diseñado por <span className="text-red-600">ex cónsules</span> + impulsado por IA
          </h2>
          <p className="text-navy-700 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed">
            Cada plan incluye el <strong className="text-navy-900">método completo Divídelo</strong>: Fase 0 con IA, perfil migratorio
            fortalecido, llenado experto de formularios y acompañamiento hasta la aprobación. Sin suscripciones. Sin cobros ocultos.
          </p>

          {/* HOY 20% DSCTO Ribbon */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mt-6 inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-red-600 via-red-700 to-blue-900 text-white shadow-2xl border-2 border-white relative overflow-hidden"
            data-testid="pricing-discount-ribbon"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse pointer-events-none" />
            <span className="text-2xl animate-bounce">🔥</span>
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-widest font-black text-red-200 leading-none">Promo exclusiva · hoy</p>
              <p className="font-heading text-xl sm:text-2xl font-black leading-tight">
                20% DSCTO <span className="text-red-200">en toda la asesoría</span>
              </p>
            </div>
            <span className="text-2xl animate-bounce" style={{ animationDelay: "0.3s" }}>🔥</span>
          </motion.div>

          {/* Trust bar */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs text-navy-600">
            <div className="flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-emerald-600" /> 95% índice de éxito</div>
            <div className="hidden sm:block w-px h-4 bg-navy-200" />
            <div className="flex items-center gap-1.5"><Users className="w-4 h-4 text-blue-700" /> +500 agentes activos</div>
            <div className="hidden sm:block w-px h-4 bg-navy-200" />
            <div className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-amber-600" /> Respuesta en 24h</div>
          </div>
        </motion.div>

        {/* Grid: Advisory (2 cards) + Advancement */}
        <div className="grid lg:grid-cols-3 gap-6 mb-14">
          {/* INDIVIDUAL */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="relative bg-white border-2 border-navy-100 rounded-3xl p-7 shadow-sm hover:shadow-xl hover:border-navy-200 transition-all flex flex-col"
            data-testid="pricing-individual"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center">
                <User className="w-6 h-6 text-navy-700" />
              </div>
              <div>
                <p className="text-navy-500 text-[11px] uppercase tracking-widest font-black">Individual</p>
                <h3 className="font-heading text-xl font-black text-navy-900 leading-none">Asesoría profesional</h3>
              </div>
            </div>

            {/* Discount badge */}
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600 text-white text-[10px] uppercase tracking-widest font-black mb-3 shadow-md">
              🔥 Hoy 20% dscto
            </div>

            <div className="mb-5">
              <div className="flex items-baseline gap-2">
                <span className="font-heading text-5xl font-black text-navy-900 tracking-tight">$79</span>
                <span className="text-navy-500 text-sm font-semibold">USD</span>
                <span className="text-navy-400 text-xl font-semibold line-through decoration-red-500 decoration-2 ml-1">$99</span>
              </div>
              <p className="text-navy-500 text-xs uppercase tracking-widest font-semibold mt-1">por persona · pago único</p>
              <p className="text-emerald-700 text-[11px] font-black mt-1.5 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                Ahorras $20 hoy
              </p>
            </div>

            <div className="h-px bg-navy-100 mb-5" />

            <p className="text-navy-700 text-sm leading-relaxed mb-4">
              Para solicitantes <strong>que viajan solos</strong>. Incluye el método completo Divídelo de principio a fin.
            </p>

            <ul className="space-y-2.5 mb-6 flex-1">
              {ADVISORY_FEATURES.slice(0, 6).map((f, i) => {
                const Icon = f.icon;
                return (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-navy-700" data-testid={`pricing-individual-feature-${i}`}>
                    <Icon className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="leading-snug">{f.label}</span>
                  </li>
                );
              })}
            </ul>

            <a href={PAYMENT_LINKS[1]} target="_blank" rel="noopener noreferrer" data-testid="pricing-individual-cta">
              <Button className="w-full bg-navy-900 hover:bg-navy-950 text-white font-bold py-5 shadow-md">
                <CreditCard className="w-4 h-4 mr-2" />
                Pagar $79 USD · 1 persona
              </Button>
            </a>

            <p className="text-center text-navy-500 text-[11px] mt-3 flex items-center justify-center gap-1.5">
              <ShieldCheck className="w-3 h-3 text-emerald-600" />
              Pago seguro Niubiz · Visa · Mastercard · Amex
            </p>
          </motion.div>

          {/* FAMILY — Highlighted */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="relative lg:scale-105 lg:-mt-2"
            data-testid="pricing-family"
          >
            {/* Floating badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
              <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-red-600 to-red-700 text-white text-[11px] uppercase tracking-widest font-black shadow-lg border border-white/30">
                ★ Más elegido ★
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-950 via-blue-900 to-red-950 text-white rounded-3xl p-7 shadow-2xl relative overflow-hidden h-full flex flex-col border-2 border-red-500/40">
              {/* Stars pattern */}
              <div className="absolute top-6 right-6 text-white/10 text-5xl select-none pointer-events-none">★</div>
              <div className="absolute bottom-10 left-6 text-white/5 text-7xl select-none pointer-events-none">★</div>

              <div className="relative flex flex-col h-full">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-red-500/20 border border-red-400/40 flex items-center justify-center">
                    <Users className="w-6 h-6 text-red-200" />
                  </div>
                  <div>
                    <p className="text-red-300 text-[11px] uppercase tracking-widest font-black">Paquete familiar</p>
                    <h3 className="font-heading text-xl font-black leading-none">Asesoría profesional · 2+ personas</h3>
                  </div>
                </div>

                {/* Discount badge */}
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white text-red-700 text-[10px] uppercase tracking-widest font-black mb-3 shadow-md">
                  🔥 Hoy 20% dscto
                </div>

                <div className="mb-5">
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading text-5xl font-black text-white tracking-tight">$60</span>
                    <span className="text-red-100 text-sm font-semibold">USD</span>
                    <span className="text-red-300/70 text-xl font-semibold line-through decoration-white decoration-2 ml-1">$75</span>
                  </div>
                  <p className="text-red-200 text-xs uppercase tracking-widest font-semibold mt-1">por persona · desde 2 solicitantes</p>
                  <div className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[10px] uppercase tracking-widest font-black">
                    <TrendingUp className="w-3 h-3" />
                    Ahorra $39/persona vs individual original
                  </div>
                </div>

                {/* Person count selector */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-5" data-testid="family-selector">
                  <p className="text-red-200 text-[11px] uppercase tracking-widest font-black mb-3 flex items-center gap-1.5">
                    <Users className="w-3 h-3" />
                    ¿Cuántas personas aplican?
                  </p>
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {[2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setFamilyCount(n)}
                        className={`py-2.5 rounded-lg font-heading font-black text-base transition-all border-2 ${
                          familyCount === n
                            ? "bg-white text-red-700 border-white shadow-lg scale-105"
                            : "bg-white/5 text-white border-white/20 hover:bg-white/10 hover:border-white/40"
                        }`}
                        data-testid={`family-selector-${n}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-red-200 line-through">
                        {familyCount} × $75 = ${familyOriginal}
                      </span>
                      <span className="font-heading text-sm font-bold text-red-300 line-through">
                        ${familyOriginal}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white">
                        <strong className="text-white">{familyCount} personas</strong> × $60
                      </span>
                      <span className="font-heading text-2xl font-black text-white">
                        ${familyTotal} <span className="text-xs text-red-200 font-semibold">USD</span>
                      </span>
                    </div>
                  </div>
                  <p className="text-emerald-300 text-[11px] mt-1 text-right font-semibold">
                    Ahorras ${familySavings} con 20% dscto
                  </p>
                </div>

                <div className="h-px bg-white/15 mb-5" />

                <p className="text-blue-100 text-sm leading-relaxed mb-4">
                  Para <strong className="text-white">familias, parejas o grupos</strong> que aplican juntos. Mismo método completo con precio preferencial por volumen.
                </p>

                <ul className="space-y-2.5 mb-6 flex-1">
                  {ADVISORY_FEATURES.map((f, i) => {
                    const Icon = f.icon;
                    return (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-blue-50" data-testid={`pricing-family-feature-${i}`}>
                        <Icon className="w-4 h-4 text-red-300 mt-0.5 flex-shrink-0" />
                        <span className="leading-snug">{f.label}</span>
                      </li>
                    );
                  })}
                </ul>

                <a href={familyPaymentUrl} target="_blank" rel="noopener noreferrer" data-testid="pricing-family-cta" key={familyCount}>
                  <Button className="w-full bg-white text-red-700 hover:bg-red-50 font-black py-5 shadow-xl text-base">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pagar ${familyTotal} USD · {familyCount} personas
                  </Button>
                </a>

                <p className="text-center text-red-200 text-[11px] mt-3 flex items-center justify-center gap-1.5">
                  <ShieldCheck className="w-3 h-3 text-emerald-300" />
                  Pago seguro Niubiz · Confirmación inmediata
                </p>
              </div>
            </div>
          </motion.div>

          {/* ADVANCEMENT */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="relative bg-white border-2 border-amber-200 rounded-3xl p-7 shadow-sm hover:shadow-xl transition-all flex flex-col"
            data-testid="pricing-advancement"
          >
            <div className="absolute top-5 right-5">
              <span className="inline-block px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] uppercase tracking-widest font-black border border-amber-300">
                Add-on
              </span>
            </div>

            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center">
                <Plane className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-amber-700 text-[11px] uppercase tracking-widest font-black">Opcional</p>
                <h3 className="font-heading text-xl font-black text-navy-900 leading-none">Adelanto de cita consular</h3>
              </div>
            </div>

            {/* Pricing breakdown */}
            <div className="bg-amber-50/50 border border-amber-100 rounded-2xl p-4 mb-5">
              <p className="text-amber-800 text-[11px] uppercase tracking-widest font-black mb-3 flex items-center gap-1.5">
                <Calculator className="w-3.5 h-3.5" />
                Cómo se cobra
              </p>
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-heading font-black text-navy-900 text-sm">Programación del sistema</p>
                    <p className="text-navy-600 text-xs leading-snug">
                      Alta del caso en nuestra plataforma de monitoreo consular 24/7
                    </p>
                    <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] uppercase tracking-widest font-black border border-amber-200">
                      Único · por caso
                    </span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-heading text-3xl font-black text-navy-900">$100</p>
                    <p className="text-navy-500 text-[10px] uppercase tracking-widest">USD</p>
                  </div>
                </div>
              </div>

              {/* What is included */}
              <div className="mt-4 pt-3 border-t border-amber-200">
                <p className="text-navy-700 text-[11px] font-semibold mb-1.5">
                  <Zap className="w-3 h-3 inline-block text-amber-600 mr-1 -mt-0.5" />
                  Aplica igual para
                </p>
                <p className="text-navy-600 text-xs leading-snug">
                  <strong className="text-navy-900">1 persona</strong> o <strong className="text-navy-900">grupo familiar completo</strong> — un solo pago cubre toda la activación del caso en el sistema.
                </p>
              </div>
            </div>

            <p className="text-navy-700 text-sm leading-relaxed mb-4">
              Add-on que trabaja en paralelo a tu asesoría. <strong>Un único pago activa el monitoreo</strong> de tu caso en la plataforma. Los días adelantados que se logren se coordinan después directamente con tu asesor.
            </p>

            <ul className="space-y-2 mb-6 flex-1">
              {ADVANCEMENT_FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-navy-700" data-testid={`pricing-adv-feature-${i}`}>
                  <CheckCircle2 className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="leading-snug">{f.label}</span>
                </li>
              ))}
            </ul>

            <a href={ADVANCEMENT_STRIPE_URL} target="_blank" rel="noopener noreferrer" data-testid="pricing-adv-cta">
              <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-5 shadow-md">
                <CreditCard className="w-4 h-4 mr-2" />
                Pagar $100 USD · Programar adelanto
              </Button>
            </a>

            <p className="text-center text-navy-500 text-[10px] mt-3 leading-relaxed px-2 italic">
              <sup>*</sup> Este pago cubre únicamente la <strong className="text-amber-700 not-italic">programación del caso en el sistema de adelanto</strong>. Los días efectivamente adelantados en el consulado se coordinan y cotizan aparte con tu asesor durante la cita Zoom, según la fecha de entrevista actual.
            </p>
          </motion.div>
        </div>

        {/* Platform benefits strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-navy-900 text-white rounded-3xl p-8 sm:p-10 relative overflow-hidden"
          data-testid="pricing-platform-strip"
        >
          <div className="absolute top-6 right-6 text-white/5 text-7xl select-none">★</div>
          <div className="absolute bottom-6 left-10 text-white/5 text-5xl select-none">★</div>

          <div className="relative grid md:grid-cols-[1.3fr_1fr] gap-8 items-center">
            <div>
              <p className="text-red-300 text-xs uppercase tracking-widest font-black mb-3">
                ★ ¿Por qué estos precios? ★
              </p>
              <h3 className="font-heading text-2xl sm:text-3xl font-black mb-4 leading-tight">
                No es un formulario — es una <span className="text-red-300">plataforma tecnológica completa</span>
              </h3>
              <p className="text-blue-100 text-base leading-relaxed">
                Detrás de cada asesoría trabaja un stack real: <strong className="text-white">motor de IA para análisis consular</strong>, biblioteca de perfiles históricos aprobados y rechazados, dashboard de monitoreo de cupos consulares, panel de agentes, integraciones con VAC/CAS y un <strong className="text-white">equipo de ex cónsules revisando manualmente cada caso.</strong>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: BrainCircuit, label: "Motor IA", sub: "Análisis consular predictivo" },
                { icon: Users, label: "Ex cónsules", sub: "Revisión manual experta" },
                { icon: TrendingUp, label: "Monitoreo", sub: "Cupos consulares 24/7" },
                { icon: ShieldCheck, label: "Garantía", sub: "No pagas si no avanzamos" },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <div
                    key={i}
                    className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors"
                    data-testid={`pricing-platform-pill-${i}`}
                  >
                    <Icon className="w-5 h-5 text-red-300 mb-2" />
                    <p className="font-heading font-black text-sm text-white leading-tight">{s.label}</p>
                    <p className="text-blue-200 text-[11px] leading-snug mt-0.5">{s.sub}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Legal disclaimer */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-navy-500 text-xs mt-8 max-w-3xl mx-auto leading-relaxed"
        >
          Precios en USD. Los aranceles consulares (ej. $185 Canadá) no están incluidos y los paga el solicitante directamente al consulado.
          El pago de la asesoría se coordina en la cita Zoom con el agente. La comisión del agente se calcula automáticamente sobre el precio pagado.
        </motion.p>
      </div>
    </section>
  );
};

export default AgentPricingSection;
